/**
 * @fileoverview Global fiscal-basis context — which fiscal year the charts mean.
 * @module contexts/FiscalBasisContext
 *
 * Same passthrough shape as TimeFilterContext: MainApp calls `useFiscalBasis()`
 * once and hands the result straight to this provider's `value`. We do NOT call
 * the hook inside the provider — that would create a second, divergent copy of
 * the selection, and the whole point is that flipping the basis in ANY view
 * flips it in all of them.
 *
 * INVARIANT: every view with a fiscal-year axis reads from this context. Never
 * hand-pass `basis` down a component tree, and always put it in the react-query
 * key so a flip refetches instead of showing last calendar's bars.
 */

import React, { createContext, useContext } from 'react';
import { DEFAULT_FISCAL_CALENDAR_KEY, getFiscalCalendar } from '@techstack/core';

/** Shape mirrors the return value of `useFiscalBasis()`. */
const FiscalBasisContext = createContext(null);

/**
 * Passthrough provider — `value` is the object returned by `useFiscalBasis()`.
 * @param {{ value: object, children: React.ReactNode }} props
 */
export function FiscalBasisProvider({ value, children }) {
  return (
    <FiscalBasisContext.Provider value={value}>
      {children}
    </FiscalBasisContext.Provider>
  );
}

/**
 * Read the global fiscal basis from anywhere under <FiscalBasisProvider>.
 * Outside a provider (tests, isolated pages) it returns the state calendar —
 * the same fallback the backend applies to an absent `basis` param, so an
 * un-provided view renders exactly what it rendered before this existed.
 * @returns {object} fiscal basis state
 */
export function useFiscalBasisContext() {
  const ctx = useContext(FiscalBasisContext);
  if (!ctx) {
    const calendar = getFiscalCalendar(DEFAULT_FISCAL_CALENDAR_KEY);
    return {
      basis: calendar.key,
      setBasis: () => {},
      calendar,
      calendars: [calendar],
      isDefaultBasis: true,
    };
  }
  return ctx;
}

export default FiscalBasisContext;
